import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import apiClient from "../../services/apiClient";

export default function SosForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const sosData = location.state?.sosData;
  const mode = location.state?.mode || (sosData?.id ? "edit" : "add");
  const isEditMode = mode === "edit";
  const isViewMode = mode === "view";

  const [formData, setFormData] = useState<any>({
    name: sosData?.name || "",
    email: sosData?.email || "",
    isd_code: sosData?.isd_code || "+91",
    phone_number: sosData?.phone_number || "",
    type: sosData?.type || "",
    additional_info: {
      Changes: sosData?.additional_info?.Changes || "",
      location: sosData?.additional_info?.location || "",
    },
    status: sosData?.status ?? 1,
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "Changes" || name === "location") {
      setFormData((prev: any) => ({
        ...prev,
        additional_info: { ...prev.additional_info, [name]: value },
      }));
    } else {
      setFormData((prev: any) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isViewMode) return;
    try {
      setLoading(true);
      if (isEditMode) {
        await apiClient(`v1/resource/sosEmergency/${sosData.id}`, {
          method: "PATCH",
          body: formData,
        });
        alert("SOS emergency updated successfully!");
      } else {
        await apiClient("v1/resource/sosEmergency", {
          method: "POST",
          body: formData,
        });
        alert("SOS emergency added successfully!");
      }
      navigate(-1);
    } catch (error) {
      console.error(error);
      alert("Failed to submit SOS emergency.");
    } finally {
      setLoading(false);
    }
  };

  const renderInputField = (
    label: string,
    name: string,
    value: string,
    placeholder: string
  ) => (
    <div className="flex-1">
      <label className="block mb-2 text-gray-700 font-semibold">{label}</label>
      <input
        type="text"
        name={name}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={isViewMode}
        className={`w-full border border-gray-300 px-4 py-3 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition ${
          isViewMode ? "bg-gray-100 cursor-not-allowed" : "bg-white"
        }`}
      />
    </div>
  );

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Card container */}
          <div className="bg-white shadow-xl rounded-2xl p-8">
            {/* Back Button */}
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="mb-6 inline-flex items-center text-blue-600 font-medium hover:underline"
            >
              &larr; Back
            </button>

            {/* Heading */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 text-center mb-8">
              {mode === "add"
                ? "Add SOS Emergency"
                : mode === "edit"
                ? "Edit SOS Emergency"
                : "View SOS Emergency"}
            </h1>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name + Email */}
              <div className="flex flex-col sm:flex-row gap-6">{renderInputField("Name", "name", formData.name, "Enter full name")}{renderInputField("Email", "email", formData.email, "Enter email address")}</div>

              {/* ISD + Phone */}
              <div className="flex flex-col sm:flex-row gap-6">{renderInputField("ISD Code", "isd_code", formData.isd_code, "e.g., +91")}{renderInputField("Phone Number", "phone_number", formData.phone_number, "Enter phone number")}</div>

              {/* Type + Status */}
              <div className="flex flex-col sm:flex-row gap-6">
                {renderInputField("Type", "type", formData.type, "Enter type (e.g., emergency)")}
                <div className="flex-1">
                  <label className="block mb-2 text-gray-700 font-semibold">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    disabled={isViewMode}
                    className={`w-full border border-gray-300 px-4 py-3 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition ${
                      isViewMode ? "bg-gray-100 cursor-not-allowed" : "bg-white"
                    }`}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>

              {/* Changes + Location */}
              <div className="flex flex-col sm:flex-row gap-6">{renderInputField("Changes", "Changes", formData.additional_info.Changes, "Enter changes")}{renderInputField("Location", "location", formData.additional_info.location, "Enter location")}</div>

              {/* Submit Button */}
              {!isViewMode && (
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-4 rounded-2xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  {loading ? "Submitting..." : isEditMode ? "Update SOS" : "Add SOS"}
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
}
